import { useMemo } from "react";
import {
  Activity,
  GitBranch,
  Sparkles,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Shield,
  Crosshair,
} from "lucide-react";
import { generateBehaviorDebrief, type BehaviorDebriefResult, type Severity } from "@/lib/debriefEngine";
import type { TrajectoryState } from "@/lib/behaviorRuntime";
import type { ForecastResponseMode } from "@/lib/forecastEngine";
import { cn } from "@/lib/utils";

interface Props {
  trajectoryHistory: TrajectoryState[];
  responseModeHistory: ForecastResponseMode[];
}

const sevText: Record<Severity, string> = {
  low: "text-cyan/85",
  moderate: "text-amber-300/90",
  high: "text-rose-400/95",
};

const sevDot: Record<Severity, string> = {
  low: "bg-cyan/80",
  moderate: "bg-amber-400/90",
  high: "bg-rose-400/95",
};

const sevBorder: Record<Severity, string> = {
  low: "border-cyan/20",
  moderate: "border-amber-500/25",
  high: "border-rose-500/30",
};

const SectionHeader = ({
  icon: Icon,
  label,
  count,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count?: number;
}) => (
  <div className="flex items-center justify-between mb-2.5">
    <div className="flex items-center gap-2">
      <Icon className="w-3 h-3 text-cyan/70" />
      <span className="text-[9.5px] font-mono tracking-[0.3em] uppercase text-muted-foreground/70">
        {label}
      </span>
    </div>
    {typeof count === "number" && (
      <span className="text-[9px] font-mono tracking-[0.22em] uppercase text-muted-foreground/40">
        {count.toString().padStart(2, "0")} signals
      </span>
    )}
  </div>
);

const Panel: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <div
    className={cn(
      "rounded-xl border border-border/40 bg-background/45 backdrop-blur-sm px-3.5 py-3",
      className,
    )}
  >
    {children}
  </div>
);

const BehavioralDebriefSections = ({ trajectoryHistory, responseModeHistory }: Props) => {
  const debrief: BehaviorDebriefResult = useMemo(
    () => generateBehaviorDebrief({ trajectoryHistory, responseModeHistory }),
    [trajectoryHistory, responseModeHistory],
  );

  if (!trajectoryHistory.length) return null;

  return (
    <section className="space-y-3" aria-label="Behavioral Debrief">
      {/* Section banner */}
      <div className="flex items-center justify-between pb-1">
        <p className="text-[10px] font-mono tracking-[0.32em] uppercase text-cyan/70">
          Behavioral Debrief
        </p>
        <p className="text-[9px] font-mono tracking-[0.22em] uppercase text-muted-foreground/45">
          T{debrief.pressureTimeline.length} · vol {debrief.metrics.avgVolatility}
        </p>
      </div>

      {/* 1 — TRAJECTORY SUMMARY */}
      <Panel className="border-cyan/25 shadow-[0_0_22px_-14px_hsl(184_100%_50%_/_0.45)]">
        <SectionHeader icon={Activity} label="Trajectory Summary" />
        <p className="text-[13px] font-light text-foreground/90 leading-relaxed">
          {debrief.trajectorySummary}
        </p>
        <div className="mt-2.5 pt-2 border-t border-border/30 flex flex-wrap gap-x-4 gap-y-1">
          {[
            ["Avg Pressure", debrief.metrics.avgPressure],
            ["Peak", debrief.metrics.peakPressure],
            ["Volatility", debrief.metrics.avgVolatility],
            ["Final Stability", debrief.metrics.finalStability],
          ].map(([label, val]) => (
            <div key={label as string} className="flex items-center gap-1.5">
              <span className="text-[9px] font-mono tracking-[0.22em] uppercase text-muted-foreground/45">
                {label}
              </span>
              <span className="text-[10px] font-mono tracking-[0.18em] text-foreground/85">
                {val}
              </span>
            </div>
          ))}
        </div>
      </Panel>

      {/* 2 — PRESSURE TIMELINE */}
      <Panel>
        <SectionHeader icon={TrendingUp} label="Pressure Timeline" count={debrief.pressureTimeline.length} />
        <ul className="space-y-1.5">
          {debrief.pressureTimeline.map((entry) => (
            <li key={entry.turn} className="flex items-start gap-2.5">
              <span className="shrink-0 text-[9.5px] font-mono tracking-[0.2em] uppercase text-muted-foreground/55 mt-px w-10">
                T{entry.turn.toString().padStart(2, "0")}
              </span>
              <span className={cn("shrink-0 w-1 h-1 rounded-full mt-1.5", sevDot[entry.severity])} />
              <div className="flex-1 min-w-0">
                <p className="text-[11.5px] text-foreground/85 leading-snug">{entry.note}</p>
                <p className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground/45 mt-0.5">
                  pressure {entry.pressure} · Δ{entry.delta >= 0 ? "+" : ""}
                  {entry.delta} · {entry.trend}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </Panel>

      {/* 3 — DETECTED COUNTERMOVES */}
      {debrief.detectedCountermoves.length > 0 && (
        <Panel>
          <SectionHeader
            icon={Crosshair}
            label="Detected Countermoves"
            count={debrief.detectedCountermoves.length}
          />
          <ul className="space-y-1.5">
            {debrief.detectedCountermoves.map((cm, i) => (
              <li
                key={i}
                className={cn(
                  "flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border bg-background/30",
                  sevBorder[cm.severity],
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[9.5px] font-mono tracking-[0.2em] uppercase text-muted-foreground/55 shrink-0">
                    T{cm.turn.toString().padStart(2, "0")}
                  </span>
                  <span className={cn("text-[11.5px] font-light truncate", sevText[cm.severity])}>
                    {cm.label}
                  </span>
                </div>
                <span className="shrink-0 text-[9px] font-mono tracking-[0.18em] uppercase text-muted-foreground/55">
                  {cm.confidence}% conf · {cm.severity}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* 4 — RESPONSE IMPACT */}
      {debrief.responseImpact.length > 0 && (
        <Panel>
          <SectionHeader icon={Shield} label="Response Impact" count={debrief.responseImpact.length} />
          <ul className="space-y-2">
            {debrief.responseImpact.map((row) => (
              <li key={row.mode} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[10.5px] font-mono tracking-[0.22em] uppercase text-foreground/80">
                    {row.modeLabel}
                  </span>
                  <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground/45">
                    {row.turns} turn{row.turns > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {row.effects.map((e, i) => (
                    <span
                      key={i}
                      className={cn(
                        "inline-flex items-center gap-1 px-1.5 py-[2px] rounded-md border text-[9.5px] font-mono uppercase tracking-[0.16em]",
                        e.sign === "+"
                          ? "border-rose-400/25 text-rose-300/85 bg-rose-500/[0.04]"
                          : "border-cyan/25 text-cyan/85 bg-cyan/[0.04]",
                      )}
                    >
                      <span className="opacity-70">{e.sign}</span>
                      {e.label}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* 5 — ALTERNATIVE PATH FORECASTS */}
      {debrief.alternativePathForecasts.length > 0 && (
        <Panel>
          <SectionHeader icon={GitBranch} label="Alternative Path Forecasts" />
          <ul className="space-y-2.5">
            {debrief.alternativePathForecasts.map((alt) => (
              <li key={alt.mode} className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-cyan/70" />
                  <span className="text-[10.5px] font-mono tracking-[0.22em] uppercase text-foreground/80">
                    If {alt.modeLabel} continued
                  </span>
                </div>
                <ul className="pl-3 space-y-0.5">
                  {alt.projections.map((p, i) => (
                    <li
                      key={i}
                      className="text-[10.5px] font-mono tracking-[0.14em] uppercase text-muted-foreground/65"
                    >
                      <span
                        className={cn(
                          "mr-1.5",
                          p.sign === "+"
                            ? "text-rose-300/80"
                            : p.sign === "-"
                            ? "text-cyan/80"
                            : "text-amber-300/80",
                        )}
                      >
                        {p.sign}
                      </span>
                      {p.label}
                    </li>
                  ))}
                </ul>
                <p className="text-[10.5px] text-muted-foreground/65 italic pl-3">{alt.summary}</p>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* 6 — STABILIZATION / ESCALATION */}
      {(debrief.stabilizationMoments.length > 0 || debrief.escalationTriggers.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Panel className="border-rose-500/20">
            <SectionHeader
              icon={AlertTriangle}
              label="Escalation Triggers"
              count={debrief.escalationTriggers.length}
            />
            {debrief.escalationTriggers.length === 0 ? (
              <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-muted-foreground/35">
                None detected.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {debrief.escalationTriggers.map((e, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[9.5px] font-mono tracking-[0.2em] uppercase text-muted-foreground/55 mt-px w-9 shrink-0">
                      T{e.turn.toString().padStart(2, "0")}
                    </span>
                    <span className={cn("shrink-0 w-1 h-1 rounded-full mt-1.5", sevDot[e.severity])} />
                    <p className="text-[11px] text-foreground/85 leading-snug">{e.note}</p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel className="border-cyan/20">
            <SectionHeader
              icon={Sparkles}
              label="Stabilization Moments"
              count={debrief.stabilizationMoments.length}
            />
            {debrief.stabilizationMoments.length === 0 ? (
              <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-muted-foreground/35">
                None detected.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {debrief.stabilizationMoments.map((e, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[9.5px] font-mono tracking-[0.2em] uppercase text-muted-foreground/55 mt-px w-9 shrink-0">
                      T{e.turn.toString().padStart(2, "0")}
                    </span>
                    <span className={cn("shrink-0 w-1 h-1 rounded-full mt-1.5", sevDot[e.severity])} />
                    <p className="text-[11px] text-foreground/85 leading-snug">{e.note}</p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      )}
    </section>
  );
};

export default BehavioralDebriefSections;
